import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUser } from "../redux/slices/authSlice";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "../styles/Register.css";

const LoginSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Required"),
  password: Yup.string().min(4, "Too Short!").required("Required"),
});

const Login = () => {
  const apiBase = process.env.REACT_APP_SERVER_URL;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [otpSent, setOtpSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [cooldown, setCooldown] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // SEND OTP
  const handleSendOtp = async (email) => {
    if (!email) {
      setErrorMessage("Please enter your email first.");
      return;
    }

    if (cooldown) return;

    setCooldown(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await axios.post(`${apiBase}/api/users/request-otp`, { email });

      setSuccessMessage(response.data.message || "OTP sent successfully!");
      setOtpSent(true);
    } catch (error) {
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage("Failed to send OTP. Try again.");
      }

      console.error("OTP send failed:", error);
    }

    setTimeout(() => setCooldown(false), 120000);
  };

  // VERIFY OTP + LOGIN
  const handleVerifyOtpAndLogin = async (values) => {
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const fullOtp = verificationCode.join("");

      if (fullOtp.length !== 6) {
        setErrorMessage("Please enter the complete 6-digit OTP.");
        return;
      }

      // verify otp
      const otpResponse = await axios.post(`${apiBase}/api/users/verify-otp`, {
        email: values.email,
        otp: fullOtp,
      });

      if (otpResponse.status === 200) {
        // login
        const loginResponse = await axios.post(`${apiBase}/api/users/login`, values);

        if (loginResponse.status === 200) {
          const userData = loginResponse.data;

          dispatch(setUser(userData));

          setSuccessMessage("Login successful!");
          navigate("/home");
        }
      }
    } catch (error) {
      console.error("Login or OTP verification failed:", error);

      // show backend message like "Invalid email or password"
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage("OTP verification or login failed.");
      }
    }
  };

  return (
    <div className="register-container">
      <h2>Login</h2>

      <Formik
        initialValues={{ email: "", password: "" }}
        validationSchema={LoginSchema}
        onSubmit={(values) => {
          handleVerifyOtpAndLogin(values);
        }}
      >
        {({ values, errors, touched }) => (
          <Form>
            {/* EMAIL */}
            <div>
              <label>Email</label>
              <Field name="email" type="email" />
              {errors.email && touched.email && <div>{errors.email}</div>}
            </div>

            {/* PASSWORD */}
            <div style={{ position: "relative" }}>
              <label>Password</label>

              <Field
                name="password"
                type={showPassword ? "text" : "password"}
                style={{ paddingRight: "30px" }}
              />

              <span
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "60%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  fontSize: "17px",
                  color: "#333",
                }}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>

              {errors.password && touched.password && <div>{errors.password}</div>}
            </div>

            {/* SEND OTP BUTTON */}
            {!otpSent ? (
              <button
                type="button"
                onClick={() => handleSendOtp(values.email)}
                disabled={cooldown}
                className={cooldown ? "otp-button disabled" : "otp-button"}
              >
                {cooldown ? "Wait..." : "Send OTP"}
              </button>
            ) : (
              <>
                {/* OTP INPUTS */}
                <div>
                  <label>Enter OTP</label>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "10px",
                      marginTop: "10px",
                    }}
                  >
                    {verificationCode.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-input-${index}`}
                        type="text"
                        value={digit}
                        maxLength={1}
                        onChange={(e) => {
                          const val = e.target.value;

                          if (/^[0-9]?$/.test(val)) {
                            const newCode = [...verificationCode];
                            newCode[index] = val;
                            setVerificationCode(newCode);

                            if (val !== "" && index < 5) {
                              document.getElementById(`otp-input-${index + 1}`).focus();
                            }
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
                            document.getElementById(`otp-input-${index - 1}`).focus();
                          }
                        }}
                        style={{
                          width: "25px",
                          height: "25px",
                          textAlign: "center",
                          fontSize: "10px",
                          borderRadius: "8px",
                          border: "1px solid #ccc",
                          outline: "none",
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* LOGIN BUTTON */}
                <button type="submit">Login</button>
              </>
            )}

            {/* ERROR / SUCCESS */}
            {errorMessage && <div style={{ color: "red" }}>{errorMessage}</div>}
            {successMessage && <div style={{ color: "green" }}>{successMessage}</div>}
          </Form>
        )}
      </Formik>

      <p>
        Forgot your password? <a href="/forgot-password">Reset here</a>
      </p>

      <p>
        New user? <a href="/register">Register here</a>
      </p>
    </div>
  );
};

export default Login;
